import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import VehicleModal from './modals/Vehicle/VehicleAddModal'; // Modal component for adding/editing vehicles
import VehicleChecklist from './modals/Vehicle/VehicleDailyChecklist/VehicleDailychecklistModal'; // Modal component for vehicle checklist
import VehicleChecklistHistoryModal from './modals/Vehicle/VehicleDailyChecklist/ViewHistory'; // Modal component for viewing checklist history
import ManageVehicleChecklist from './modals/Vehicle/VehicleDailyChecklist/ManageVehicleChecklist'; // Updated path for manage checklist modal
import Pagination from '../Tools/Pagination'; // Component for pagination controls
import ChecklistHistoryModal from './modals/Vehicle/VehicleDailyChecklist/ChecklistHistoryModal'; // New modal for checklist history list

// LocationDropdown component for selecting locations
const LocationDropdown = ({ onChange, value }) => {
  const [locations, setLocations] = useState([]); // Stores list of locations
  const [loading, setLoading] = useState(false); // Loading state for fetching locations

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true); // Set loading state
        const response = await axiosInstance.get('/ems/locations'); // Fetch locations
        setLocations(response.data?.data || response.data || []); // Handle different response formats
      } catch (error) {
        console.error('Error fetching locations:', error); // Log error
        setLocations([]); // Reset locations on error
      } finally {
        setLoading(false); // Clear loading state
      }
    };

    fetchLocations();
  }, []); // Empty dependency array ensures run on mount only

  // Render dropdown for locations
  return (
    <select
      className="form-control form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)} // Trigger onChange with selected value
      disabled={loading} // Disable during loading
    >
      <option value="">All Locations</option>
      {locations.map((location) => (
        <option key={location.id} value={location.id}>
          {location.location_name}
        </option>
      ))}
    </select>
  );
};

// VehicleTab component for managing EMS vehicles
const VehicleTab = () => {
  const [vehicles, setVehicles] = useState([]); // Stores list of vehicles
  const [filteredData, setFilteredData] = useState([]); // Stores filtered vehicles
  const [formData, setFormData] = useState({
    plate_number: '', // Vehicle plate number
    type: '', // Vehicle type
    location_id: '', // Location ID
    medical_center_id: '', // Medical center ID
    status: '' // Vehicle status
  }); // Form data for vehicle creation/editing
  const [editId, setEditId] = useState(null); // ID of vehicle being edited
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentChecklistVehicle, setCurrentChecklistVehicle] = useState(null); // ID of vehicle for checklist/history
  const [currentManageVehicleId, setCurrentManageVehicleId] = useState(null); // ID of vehicle for manage checklist modal
  const [showManageModal, setShowManageModal] = useState(false); // Controls visibility of manage checklist modal
  const [refreshHistory, setRefreshHistory] = useState(false); // Trigger to refresh history view

  const [filterForm, setFilterForm] = useState({
    plate_number: '', // Filter by plate number
    location_id: '', // Filter by location ID
    status: '' // Filter by status
  }); // Filter criteria for vehicles

  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const itemsPerPage = 10; // Number of items per page
  const roleName = sessionStorage.getItem("Role_Name");

  // Get checklist type from sessionStorage
  const checklistType = sessionStorage.getItem('checklist_type');
  // Show checklist column: for "EMS Team", only when checklistType is 'vehicle'; for other roles, always show
  const showChecklistColumn = roleName === 'EMS Team' ? checklistType === 'vehicle' : true;

  // Fetch vehicles from API
  const fetchVehicles = async () => {
    try {
      setIsLoading(true); // Set loading state
      const roleName = sessionStorage.getItem('Role_Name'); // Get user role
      const userId = sessionStorage.getItem('user_id') || ''; // Get user ID
      const params = {
        user_id: roleName === 'EMS Resource Manager' ? userId : '' // Conditional user_id
      };

      // Add filter parameters if set
      if (filterForm.plate_number) params.plate_number = filterForm.plate_number;
      if (filterForm.location_id) params.location_id = filterForm.location_id;
      if (filterForm.status !== '') params.status = filterForm.status;

      // Fetch vehicles
      const response = await axiosInstance.get('/ems/vehicles', { params });

      const data = response.data?.data || response.data || []; // Handle different response formats
      setVehicles(data); // Update vehicles state
      setFilteredData(data); // Update filtered data
    } catch (error) {
      console.error('Error fetching vehicles:', error); // Log error
      alert('Failed to fetch vehicles'); // Show alert
      setVehicles([]); // Reset vehicles
      setFilteredData([]); // Reset filtered data
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Apply client-side filters
  const applyFilters = () => {
    let data = [...vehicles];

    // Filter by plate number (case-insensitive)
    if (filterForm.plate_number) {
      data = data.filter(d => d.plate_number?.toLowerCase().includes(filterForm.plate_number.toLowerCase()));
    }

    // Filter by location ID
    if (filterForm.location_id) {
      data = data.filter(d => d.location_id?.toString() === filterForm.location_id);
    }

    // Filter by status
    if (filterForm.status !== '') {
      data = data.filter(d => d.status?.toString() === filterForm.status);
    }

    setFilteredData(data); // Update filtered data
    setCurrentPage(1); // Reset to first page
  };

  // Fetch vehicles on mount
  useEffect(() => {
    fetchVehicles();
  }, []); // Empty dependency array ensures run on mount only

  // Apply filters when filterForm or vehicles change
  useEffect(() => {
    applyFilters();
  }, [filterForm, vehicles]); // Re-run when filterForm or vehicles change

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFormData((prev) => ({ ...prev, [name]: value })); // Update form data
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFilterForm(prev => ({ ...prev, [name]: value })); // Update filter form
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterForm({
      plate_number: '',
      location_id: '',
      status: ''
    }); // Reset filter fields
    setCurrentPage(1); // Reset to first page
  };

  // Open modal for adding a new vehicle
  const openAddModal = () => {
    setEditId(null); // Clear edit ID
    setFormData({ 
      plate_number: '', 
      type: '', 
      location_id: '', 
      medical_center_id: '',
      status: '' 
    }); // Reset form data
    showModal('bs-vehicles'); // Show vehicle modal
  };

  // Prepare modal for editing an existing vehicle
  const handleEdit = (item) => {
    setEditId(item.id); // Set edit ID
    setFormData({ 
      plate_number: item.plate_number, 
      type: item.type, 
      location_id: item.location_id, 
      medical_center_id: item.medical_center_id || '',
      status: item.status 
    }); // Populate form with vehicle data
    showModal('bs-vehicles'); // Show vehicle modal
  };

  // Show Bootstrap modal by ID
  const showModal = (modalId) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl && window.bootstrap?.Modal) {
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl); // Initialize or get modal instance
      modal.show(); // Show modal
    } else {
      console.warn('Bootstrap modal not initialized properly.'); // Warn if modal fails
    }
  };

  // Hide Bootstrap modal by ID
  const hideModal = (modalId) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl && window.bootstrap?.Modal) {
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide(); // Hide modal
    }
  };

  // Delete a vehicle with confirmation
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await axiosInstance.delete(`/ems/vehicles/${id}`); // Delete vehicle
        alert('Deleted successfully'); // Show success alert
        fetchVehicles(); // Refresh vehicle list
      } catch (err) {
        console.error('Delete failed:', err); // Log error
        alert('Failed to delete'); // Show error alert
      }
    }
  };

  // Save vehicle (add or update)
  const handleSave = async () => {
    try {
      const userId = sessionStorage.getItem('user_id') || ''; // Get user ID
      const payload = new URLSearchParams();
      payload.append('plate_number', formData.plate_number);
      payload.append('type', formData.type);

      // Enforce rule: only one of location_id or medical_center_id
      if (formData.location_id) {
        payload.append('location_id', formData.location_id);
        payload.append('medical_center_id', 0);
      } else if (formData.medical_center_id) {
        payload.append('medical_center_id', formData.medical_center_id);
        payload.append('location_id', 0);
      }

      payload.append('status', formData.status);
      payload.append('user_id', userId);

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded', // Set content type for URL-encoded payload
      };

      if (editId) {
        await axiosInstance.put(`/ems/vehicles/${editId}`, payload, { headers }); // Update vehicle
        alert('Updated successfully'); // Show success alert
      } else {
        await axiosInstance.post('/ems/vehicles', payload, { headers }); // Create vehicle
        alert('Added successfully'); // Show success alert
      }

      const modalEl = document.getElementById('bs-vehicles');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      modal.hide(); // Hide modal
      fetchVehicles(); // Refresh vehicle list
    } catch (err) {
      console.error('Save failed:', err); // Log error
      alert('Failed to save'); // Show error alert
    }
  };

  // Open checklist modal for a vehicle
  const handleOpenChecklist = (vehicleId) => {
    sessionStorage.setItem('currentVehicleId', vehicleId); // Store vehicle ID in session
    setCurrentChecklistVehicle(vehicleId); // Set current vehicle for checklist
    showModal('bs-daily-checklist'); // Show checklist modal
  };

  // Open checklist history list modal for a vehicle
  const handleOpenHistory = (vehicleId) => {
    sessionStorage.setItem('currentVehicleId', vehicleId); // Store vehicle ID in session
    setCurrentChecklistVehicle(vehicleId); // Set current vehicle for history
    setRefreshHistory(prev => !prev); // Toggle refresh trigger
    showModal('bs-checklist-history-list'); // Show new history list modal
  };

  // Open detailed checklist history modal from history list
  const handleOpenDetailedHistory = (checklistId) => {
    sessionStorage.setItem('currentChecklistId', checklistId); // Store checklist ID in session
    hideModal('bs-checklist-history-list'); // Hide history list modal
    showModal('bs-checklist-history'); // Show detailed history modal
  };

  // Open manage checklist modal for a vehicle
  const handleManageChecklist = (vehicleId) => {
    sessionStorage.setItem('currentVehicleId', vehicleId); // Store vehicle ID in session
    setCurrentManageVehicleId(vehicleId); // Set vehicle ID for manage modal
    setShowManageModal(true); // Show manage modal
  };

  // Close the manage checklist modal
  const handleCloseManageModal = () => {
    sessionStorage.removeItem('currentVehicleId'); // Clear vehicle ID from session
    setShowManageModal(false); // Hide manage modal
    setCurrentManageVehicleId(null); // Clear manage vehicle ID
  };

  // Convert status code to label
  const getStatusLabel = (status) => {
    switch (status) {
      case '0':
      case 0:
        return 'Busy';
      case '1':
      case 1:
        return 'Ready for Service';
      case '2':
      case 2:
        return 'Under Maintenance';
      default:
        return 'Unknown';
    }
  };

  // Pagination calculations
  const totalItems = filteredData.length; // Total number of filtered vehicles
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  ); // Vehicles for current page

  // Render the component
  return (
    <div className="tab-pane active" id="specialities" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input 
                type="text" 
                className="form-control" 
                name="plate_number"
                placeholder="Plate No." 
                value={filterForm.plate_number}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <LocationDropdown
                onChange={(value) => setFilterForm(prev => ({ 
                  ...prev, 
                  location_id: value === "" ? null : value 
                }))} // Update location_id in filter form
                value={filterForm.location_id}
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <select 
                className="form-control form-select"
                name="status"
                value={filterForm.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="0">Busy</option>
                <option value="1">Ready for service</option>
                <option value="2">Under Maintenance</option>
              </select>
            </div>
            <div className="form-group col-lg-3 p-2">
              <button 
                type="button" 
                className="btn btn-outline-primary btn-rounded"
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
           {roleName !== "EMS Team" && (
          <button
            className="btn my-2 d-flex align-items-center justify-content-center btn-outline-primary"
            type="button"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Vehicle
          </button>
           )}
        </div>
      </div>

      {/* Vehicle Table */}
      {isLoading ? (
        <div className="text-center py-4">Loading Vehicles...</div>
      ) : (
        <>
          <div className="py-3 px-0">
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Plate No.</h6></th>
                    <th><h6 className="fw-semibold mb-0">Location</h6></th>
                    <th><h6 className="fw-semibold mb-0">Medical Center</h6></th>
                    <th><h6 className="fw-semibold mb-0">Type</h6></th>
                    <th><h6 className="fw-semibold mb-0">Status</h6></th>
                    {showChecklistColumn && (
                      <th colSpan="3" className="text-center"><h6 className="fw-semibold mb-0">Checklist</h6></th>
                    )}
                    {roleName !== "EMS Team" && <th><h6 className="fw-semibold mb-0">Action</h6></th>}

                  </tr>
                </thead>  
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={showChecklistColumn ? 9 : 8} className="text-center py-4">
                        No Vehicles Found
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{item.plate_number}</td>
                        <td>{item.ems_location?.location_name || 'N/A'}</td>
                        <td>{item.medical_center?.name || 'N/A'}</td>
                        <td>{item.type}</td>
                        <td>{getStatusLabel(item.status)}</td>
                        {showChecklistColumn && (
                          <td colSpan="3">
                            <div className="d-flex justify-content-center align-items-center">
                              <p className="mb-0 fw-normal">
                                <a className="text-primary" onClick={() => handleOpenChecklist(item.id)} style={{ cursor: 'pointer' }}>
                                  Start Checklist
                                </a>
                              </p>
                              <span className="mx-1"> | </span>
                              <p className="mb-0 fw-normal">
                                <a className="text-primary" onClick={() => handleOpenHistory(item.id)} style={{ cursor: 'pointer' }}>
                                  View History
                                </a>
                              </p>
                              {roleName !== 'EMS Team' && (
                                <>
                                  <span className="mx-1"> | </span>
                                  <p className="mb-0 fw-normal">
                                    <a 
                                      className="text-primary" 
                                      onClick={() => handleManageChecklist(item.id)} 
                                      style={{ cursor: 'pointer' }}
                                    >
                                      Manage Checklist
                                    </a>
                                  </p>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                        {roleName !== "EMS Team" && (
                        <td>
                          <button onClick={() => handleEdit(item)} className="h4 me-3 border-0 bg-transparent">
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button onClick={() => handleDelete(item.id)} className="h4 border-0 bg-transparent">
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && (
            <Pagination
              total={totalItems}
              limit={itemsPerPage}
              page={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Vehicle Add/Edit Modal */}
      <VehicleModal
        formData={formData}
        onChange={handleChange}
        onSave={handleSave}
        editId={editId}
      />

      {/* Vehicle Checklist Modal - Only render if checklist column is shown */}
      {showChecklistColumn && (
        <>
          <VehicleChecklist
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            editId={editId}
            vehicleId={currentChecklistVehicle}
          />

          {/* New Checklist History List Modal */}
          <ChecklistHistoryModal
            vehicleId={currentChecklistVehicle}
            onViewDetails={handleOpenDetailedHistory}
          />

          {/* Detailed Checklist History Modal */}
          <VehicleChecklistHistoryModal 
            vehicleId={currentChecklistVehicle}
            refreshTrigger={refreshHistory}
          />

          {/* Manage Vehicle Checklist Modal */}
          {showManageModal && (
            <ManageVehicleChecklist
              vehicleId={currentManageVehicleId}
              onClose={handleCloseManageModal}
            />
          )}
        </>
      )}
    </div>
  );
};

// Export the component
export default VehicleTab;